/**
 * Scroll-intent classification and the auto-follow hysteresis band.
 *
 * A `scroll` event carries no source: the follow pin's ResizeObserver writes,
 * the open-on-latest pin loop and a real wheel all look identical to a listener. So
 * intent is read from the *input* events instead, and the distance thresholds
 * below only ever run for a scroll a user actually caused.
 *
 * Wheel, touch and keyboard are the complete set of user scroll sources here:
 * the list renders in a `FadingScrollable`, which hides the scrollbar gutter
 * (`invisible-scrollbar`) and leaves `dragScroll` off, so there is nothing to
 * drag with a pointer.
 */

export type ScrollIntentDirection = 'up' | 'down'

/** Below this many pixels a touch move is jitter, not a drag. */
const TOUCH_DRAG_THRESHOLD_PX = 4

/** Re-engage follow only this close to the bottom. */
export const AUTO_SCROLL_ENGAGE_DISTANCE_PX = 50
/** Stay engaged until the user is at least this far from the bottom. */
export const AUTO_SCROLL_DISENGAGE_DISTANCE_PX = 150

/** Each classifier returns null for input that isn't a vertical scroll at all. */
export function wheelIntentDirection(deltaY: number): ScrollIntentDirection | null {
  if (deltaY < 0) return 'up'
  if (deltaY > 0) return 'down'
  return null
}

export function keyIntentDirection(key: string): ScrollIntentDirection | null {
  switch (key) {
    case 'ArrowUp':
    case 'PageUp':
    case 'Home':
      return 'up'
    case 'ArrowDown':
    case 'PageDown':
    case 'End':
    case ' ':
    case 'Spacebar':
      return 'down'
    default:
      return null
  }
}

/** A finger travelling down the screen pulls earlier content in — that's scrolling up. */
export function touchIntentDirection(startY: number, currentY: number): ScrollIntentDirection | null {
  const delta = currentY - startY
  if (delta > TOUCH_DRAG_THRESHOLD_PX) return 'up'
  if (delta < -TOUCH_DRAG_THRESHOLD_PX) return 'down'
  return null
}

/**
 * Follow state after a *downward* user scroll.
 *
 * Upward intent never gets here: leaving the bottom is always deliberate, so it
 * disengages with no measurement at all, on the input event itself. That leaves
 * one question — is the user back close enough to follow again, or drifted far
 * enough to stop? — answered across a hysteresis band, because a single
 * threshold flipped the state on any wobble around it.
 */
export function nextAutoScrollState({
  isFollowing,
  distanceFromBottom,
  contentGrew,
}: {
  isFollowing: boolean
  distanceFromBottom: number
  /** Did the list get taller since the previous measurement? */
  contentGrew: boolean
}): boolean {
  // Distance from the bottom grows two ways: the user leaves it, or the content
  // does. A frame that gained height can't tell them apart, so follow holds
  // rather than reading the stream's own growth as the user walking away.
  if (isFollowing) return contentGrew || distanceFromBottom <= AUTO_SCROLL_DISENGAGE_DISTANCE_PX
  return distanceFromBottom < AUTO_SCROLL_ENGAGE_DISTANCE_PX
}
